import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { AppContext } from "../../context/AppContext";
import Shop from "../shop";
import ProductInfo from "./ProductCard/ProductInfo";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart } = useContext(AppContext);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const slugify = (text) =>
    (typeof text === "string" ? text : text?.name || "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");

  const selectedVariant = product?.variants?.find(
    (v) => String(v.id) === String(selectedVariantId)
  );

  const sectionSlug = slugify(product.section);
  const categorySlug = slugify(product.category);

  const handleViewDetails = () => {
    if (selectedVariant?.slug) {
      navigate(
        `/categorias/${sectionSlug}/${categorySlug}/${product.slug}/${selectedVariant.slug}`
      );
    } else {
      navigate(`/categorias/${sectionSlug}/${categorySlug}/${product.slug}`);
    }
  };

  const promoType = product?.tipo_promocion || null;
  const promoVal = Number(product?.valor_promocion ?? 0);

  const handleAddToCart = (e) => {
    e.stopPropagation();

    const url = selectedVariant
      ? `/categorias/${sectionSlug}/${categorySlug}/${product.slug}/${selectedVariant.slug}`
      : `/categorias/${sectionSlug}/${categorySlug}/${product.slug}`;

    const unitPriceForCart = selectedVariant
      ? selectedVariant.precio_descuento ?? selectedVariant.price
      : product.precio_descuento ?? product.pvp1;

    const iva_porcentaje = selectedVariant
      ? selectedVariant.iva_aplicado_porcentaje ?? selectedVariant.iva_porcentaje
      : product.iva_aplicado_porcentaje ?? product.iva_porcentaje;

    const iva_valor = selectedVariant
      ? selectedVariant.iva_aplicado_valor ?? selectedVariant.iva_valor
      : product.iva_aplicado_valor ?? product.iva_valor;

    const payload = selectedVariant
      ? {
          id: `${product.id}-${selectedVariant.id}`,
          name: product.name,
          code: product.code,
          sku: selectedVariant.sku ?? "",
          price: Number(unitPriceForCart),
          quantity: 1,
          section: product.section,
          category: product.category,
          brand: product.brand,
          images: selectedVariant.image ? [selectedVariant.image] : product.images || [],
          variant: {
            type: product.variants[0].variant_type,
            value: selectedVariant.value,
          },
          variantUrl: url,
          promocion: promoType,
          descuento: promoVal,
          iva_porcentaje,
          iva_valor,
        }
      : {
          id: product.id,
          name: product.name,
          code: product.code,
          price: Number(unitPriceForCart),
          quantity: 1,
          section: product.section,
          category: product.category,
          brand: product.brand,
          images: product.images || [],
          variantUrl: url,
          promocion: promoType,
          descuento: promoVal,
          iva_porcentaje,
          iva_valor,
        };

    addToCart(payload, 1);
    setIsCartOpen(true);
  };

  const displayImage = selectedVariant?.image || product.images?.[0] || "";

  const badge = (() => {
    const baseSx = {
      position: "absolute",
      top: { xs: 6, sm: 10 },
      left: { xs: 6, sm: 10 },
      px: { xs: 1, sm: 1.5 },
      py: { xs: 0.25, sm: 0.5 },
      borderRadius: "20px",
      color: "#fff",
      fontWeight: 700,
      fontSize: { xs: "0.72rem", sm: "0.8rem" },
      zIndex: 10,
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      maxWidth: { xs: "78%", sm: "unset" },
      lineHeight: 1.1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };
    if (promoType === "descuento" && promoVal > 0) {
      return (
        <Box sx={{ ...baseSx, bgcolor: "#E53935" }}>
          Descuento {Math.round(promoVal)}%
        </Box>
      );
    }
    if (promoType === "precio_fijo" && promoVal > 0) {
      return <Box sx={{ ...baseSx, bgcolor: "#ed6c02" }}>-${promoVal.toFixed(2)}</Box>;
    }
    if (promoType === "destacado") {
      return <Box sx={{ ...baseSx, bgcolor: "#1976d2" }}>Destacado</Box>;
    }
    if (promoType === "mas_vendido") {
      return <Box sx={{ ...baseSx, bgcolor: "#2e7d32" }}>Más vendido</Box>;
    }
    return null;
  })();

  return (
    <>
      <Box
        onClick={handleViewDetails}
        sx={{
          border: "1px solid #E5E5E5",
          borderRadius: 2,
          p: { xs: 1, sm: 2 },
          cursor: "pointer",
          width: { xs: "100%", sm: 240 },
          mx: { xs: 0, sm: 1 },
          my: { xs: 1, sm: 2 },
          position: "relative",
          background: "#fff",
          boxShadow: { xs: "0 2px 8px rgba(0,0,0,0.06)", sm: "0 4px 12px rgba(0,0,0,0.1)" },
          display: "grid",
          gridTemplateColumns: { xs: "42% 1fr", sm: "1fr" },
          columnGap: { xs: 1.25, sm: 0 },
          rowGap: { xs: 0.5, sm: 0 },
          alignItems: "center",
          textAlign: { xs: "left", sm: "center" },
        }}
      >
        {badge}

        {displayImage && (
          <Box
            sx={{
              gridColumn: { xs: "1 / 2", sm: "1 / -1" },
              width: "100%",
              height: { xs: 96, sm: 160, md: 180 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={displayImage}
              alt={product.name}
              loading="lazy"
              decoding="async"
              sx={{
                height: "100%",
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            gridColumn: { xs: "2 / 3", sm: "1 / -1" },
            width: "100%",
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <ProductInfo
            product={product}
            isMobile={isMobile}
            selectedVariant={selectedVariant}
            selectedVariantId={selectedVariantId}
            setSelectedVariantId={setSelectedVariantId}
            handleAddToCart={handleAddToCart}
          />
        </Box>
      </Box>

      <Shop isOpen={isCartOpen} toggleDrawer={() => setIsCartOpen(!isCartOpen)} />
    </>
  );
}
